import { apiClient } from '@api/apiClient';
import { PricingOptionsArray } from '@common/commonLists';
import { isMaxRateEnforcementEnabled } from '@common/order/validations';
import { ToastType } from '@components';
import {
  AllBids,
  CreateOfferForLoadResponseT,
  SideCardBidT,
  TruckBidT,
} from '@store/capacityTools/types';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  getCarrierDetail,
  index,
  load,
  offerStore,
  orderStore,
  setOfferStore,
  setOrderStore,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { userStore } from '@store/user';
import { CarrierAutoCompleteModel } from '@typeDefinitions/carrierTypes';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { LineItemViewModel } from '@typeDefinitions/lineItemTypes';
import { handleToast } from '@utils/utils';
import { handleError } from '@views/order/helpers';
import {
  applyOfferManagerCoveredBy,
  applyOwnedCarrierRep,
  isOwnedCapacityCoveredByEnabled,
} from '@views/order/components/carrier/loads/LoadInfo/utils';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

export function mapCreateOfferForLoadResponseToSideCardBid(
  response: CreateOfferForLoadResponseT,
  carrier: CarrierAutoCompleteModel,
): SideCardBidT {
  return {
    id: response.id,
    userId: response.createdBy,
    truckId: response.truckId,
    status: 'Pending',
    bidType: response.bidType ?? '',
    amount: response.amount,
    requestedBy: userStore.user.name,
    createdDate: response.createdDate,
    reviewedDate: response.reviewedDate,
    carrierName: carrier.name,
    carrierStatus: carrier.status,
    mc: carrier.mc,
    dot: carrier.dot,
    primaryContact: '',
    primaryPhone: '',
    originLatitude: null,
    originLongitude: null,
    originRadiusMiles: null,
    destinationRadiusMiles: null,
    destinationLatitude: null,
    destinationLongitude: null,
    counterAmount: response.counterAmount,
    counteredBy: null,
    externalContactName: response.externalContactName ?? null,
    externalContactEmail: response.externalContactEmail ?? null,
    externalContactPhone: response.externalContactPhone ?? null,
    externalPreferredContactMethod:
      response.externalPreferredContactMethod ?? null,
    pointOfSale: '',
    carrierId: response.carrierId,
  };
}

let loadOffersGeneration = 0;

export function invalidateLoadOffersRequests() {
  loadOffersGeneration++;
}

export async function getLoadOffers(loadId: number) {
  const generation = ++loadOffersGeneration;
  try {
    const response: { value: AllBids } = await apiClient.get(
      `/capacity/truck/bid/load/allbids/${loadId}`,
    );
    if (generation === loadOffersGeneration) {
      setOfferStore(response.value);
    }
    return response.value.bids;
  } catch (error: unknown) {
    if (generation === loadOffersGeneration) {
      handleError(error);
    }
    return undefined;
  }
}

// Covered By on carrier match only. Does not write offerStore or advance
// loadOffersGeneration, so a tab switch cannot replace the active load's bids.
export async function fetchLoadBids(loadId: number) {
  try {
    const response: { value: AllBids } = await apiClient.get(
      `/capacity/truck/bid/load/allbids/${loadId}`,
    );
    return response.value.bids;
  } catch (error: unknown) {
    handleError(error);
    return undefined;
  }
}

export const isLoadAvailable = () => {
  return load()?.status === 'Can Get' || load()?.status === 'Unassigned';
};

export async function submitBid(
  carrier: CarrierAutoCompleteModel,
  amount: number,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .orderDetails.carrierOffersSubmitBid,
) {
  try {
    const response: { value: CreateOfferForLoadResponseT } =
      await apiClient.post(
        '/capacity/create-offer-for-load',
        {
          carrierId: carrier.id,
          amount,
          loadId: load()?.id,
        },
        buildChangeTrackingRequestConfig([changeTrackingSource]),
      );
    const newBid = mapCreateOfferForLoadResponseToSideCardBid(
      response.value,
      carrier,
    );
    setOfferStore('bids', offerStore.bids.length, newBid);
    handleToast(ToastType.Success, 'Successfully submitted bid');
  } catch (error: unknown) {
    handleError(error);
  }
}

export async function approveCounter(
  offerId: number,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .orderDetails.carrierOffersApproveCounter,
) {
  try {
    await apiClient.post(
      `/capacity/truck/bid/${offerId}/approvecounter`,
      undefined,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    const ind = offerStore.bids.findIndex((bid) => bid.id === offerId);
    if (ind >= 0) {
      const bid = offerStore.bids[ind];
      setOfferStore('bids', ind, {
        status: 'Approved',
        amount: bid.counterAmount,
      });
    }
    handleToast(ToastType.Success, 'Successfully approved counter');
  } catch (error: unknown) {
    handleError(error);
  }
}

export async function expireBid(
  offerId: number,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .orderDetails.carrierOffersExpireBid,
) {
  try {
    await apiClient.post(
      `/capacity/truck/bid/${offerId}/cancel`,
      undefined,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    const ind = offerStore.bids.findIndex((bid) => bid.id === offerId);
    if (ind >= 0) {
      setOfferStore('bids', ind, { status: 'Expired' });
      handleToast(ToastType.Success, 'Successfully expired bid');
    }
  } catch (error: unknown) {
    handleError(error);
  }
}

export const rejectOffer = async (
  offerId: number,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .orderDetails.carrierOffersRejectOffer,
) => {
  try {
    await apiClient.post(
      `/capacity/truck/bid/${offerId}/reject`,
      undefined,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    const ind = offerStore.bids.findIndex((bid) => bid.id === offerId);
    if (ind >= 0) {
      setOfferStore('bids', ind, {
        status: 'Rejected',
        counteredBy: userStore.user.name,
        reviewedDate: new Date().toISOString(),
      });
      handleToast(ToastType.Success, 'Successfully rejected offer');
    }
  } catch (error: unknown) {
    handleError(error);
  }
};

export const counterOffer = async (
  offerId: number,
  amount: number,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .orderDetails.carrierOffersCounterOffer,
) => {
  try {
    await apiClient.put(
      `/capacity/truck/bid/${offerId}/counter`,
      amount,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    const ind = offerStore.bids.findIndex((bid) => bid.id === offerId);
    if (ind >= 0) {
      setOfferStore('bids', ind, {
        status: 'Countered',
        amount: amount,
        counterAmount: null,
        counteredBy: userStore.user.name,
        reviewedDate: new Date().toISOString(),
      });
      handleToast(ToastType.Success, 'Successfully countered offer');
    }
  } catch (error: unknown) {
    handleError(error);
  }
};

export const approveOffer = async (
  offerId: number,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .orderDetails.carrierOffersApproveOffer,
) => {
  try {
    await apiClient.post(
      `/capacity/truck/bid/${offerId}/approve`,
      undefined,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    const ind = offerStore.bids.findIndex((bid) => bid.id === offerId);
    if (ind >= 0) {
      setOfferStore('bids', ind, {
        status: 'Approved',
        counteredBy: userStore.user.name,
        reviewedDate: new Date().toISOString(),
      });
      handleToast(ToastType.Success, 'Successfully approved bid');
    }
  } catch (error: unknown) {
    handleError(error);
  }
};

export const editBid = async (
  offerId: number,
  amount: number,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .orderDetails.carrierOffersEditBid,
) => {
  try {
    const response: { value: TruckBidT } = await apiClient.put(
      `/capacity/truck/bid/${offerId}/edit`,
      amount,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    const ind = offerStore.bids.findIndex((bid) => bid.id === offerId);
    if (ind >= 0) {
      setOfferStore('bids', ind, {
        amount: response.value.amount,
        status: 'Pending',
        counterAmount: null,
        counteredBy: null,
      });
      handleToast(ToastType.Success, 'Successfully edited bid');
    }
  } catch (error: unknown) {
    handleError(error);
  }
};

export const counterAmountStyles = (counterAmount: number | null) => {
  if (counterAmount != null) {
    return 'text-[#ffa000]';
  }
  return '';
};

const autoPopulateCarrierChargeFromOffer = (
  bid: SideCardBidT,
  loadIndex: number,
) => {
  const amount = bid.counterAmount ?? bid.amount;
  const targetLoad = orderStore.order.loads.at(loadIndex);

  if (
    amount == null ||
    !targetLoad ||
    !isFeatureFlagEnabled(
      FeatureFlags.EnableOfferManagerAutoPopulateCarrierCharge,
    )()
  ) {
    return;
  }

  const isReplaceable = (item: LineItemViewModel) =>
    Boolean(item.type) &&
    PricingOptionsArray.includes(item.type!) &&
    !item.readOnly;

  const lineItems = (targetLoad.lineItems ?? [])
    .filter((item) => !isReplaceable(item) || item.id > 0)
    .map((item) =>
      isReplaceable(item)
        ? { ...item, operationType: 'Delete' as const }
        : item,
    );

  updateLoadPropertyAtIndex(
    {
      lineItems: [
        ...lineItems,
        {
          operationType: 'Insert',
          quantity: 1,
          type: 'Line Haul',
          description: '',
          rate: amount,
          estimate: false,
          id: 0,
          readOnly: false,
          synched: false,
          adjustment: false,
          stopId: 0,
          status: 'Unsettled',
          pickUpId: undefined,
          dropOffId: undefined,
        },
      ],
    },
    loadIndex,
  );
};

const withBookedBid = (
  existing: TruckBidT[] | undefined,
  bid: SideCardBidT,
  loadId: number,
): TruckBidT[] => {
  const bids = [...(existing ?? [])];
  const at = bids.findIndex((item) => item.id === bid.id);
  const booked = {
    status: 'Booked',
    amount: bid.amount,
    counterAmount: bid.counterAmount,
  };

  if (at >= 0) {
    bids[at] = { ...bids[at], ...booked };
    return bids;
  }

  bids.push({
    ...booked,
    id: bid.id,
    truckId: bid.truckId,
    carrierId: bid.carrierId,
    loadId,
    bidType: bid.bidType,
    archived: false,
    reviewedBy: null,
    reviewedDate: bid.reviewedDate,
    createdDate: bid.createdDate,
    createdBy: bid.userId,
    modifiedDate: null,
    modifiedBy: null,
    requestedBy: bid.requestedBy,
    noteKeyValues: [],
  });
  return bids;
};

export const bookLoad = async (
  offerId: number,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .orderDetails.carrierOffersBookLoad,
) => {
  const selectedBid = offerStore.bids.find((bid) => bid.id === offerId);
  const bookedBid = selectedBid ? { ...selectedBid } : null;
  const loadIndex = index();
  try {
    await apiClient.post(
      `/capacity/truck/bid/${offerId}/book`,
      undefined,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    const ind = offerStore.bids.findIndex((bid) => bid.id === offerId);
    if (ind >= 0) {
      setOfferStore('bids', ind, { status: 'Booked' });
    }
    if (bookedBid) {
      setOrderStore('matched', true);
      const carrierDetail = await getCarrierDetail(
        bookedBid.carrierId,
        loadIndex,
      );
      const targetLoad = orderStore.order.loads[loadIndex];
      updateLoadPropertyAtIndex(
        {
          carrierId: bookedBid.carrierId,
          status: 'Assigned',
          // Booked but not saved yet, so it can still be cleared before saving.
          ...(isFeatureFlagEnabled(
            FeatureFlags.EnableLoadDetailsCarrierBounceTracking,
          )() && { isCarrierDirty: true }),
          ...(isMaxRateEnforcementEnabled() && {
            truckBids: withBookedBid(
              targetLoad.truckBids,
              bookedBid,
              targetLoad.id,
            ),
          }),
        },
        loadIndex,
      );
      if (isOwnedCapacityCoveredByEnabled(targetLoad.mode)) {
        if (carrierDetail !== undefined) {
          applyOfferManagerCoveredBy(
            carrierDetail,
            bookedBid,
            targetLoad.hasBeenSharedWithCapacity,
            loadIndex,
          );
        }
      } else {
        await applyOwnedCarrierRep(bookedBid.carrierId, loadIndex);
      }
      autoPopulateCarrierChargeFromOffer(bookedBid, loadIndex);
    }
    handleToast(
      ToastType.Success,
      'Successfully booked load, please save the order to complete the process',
    );
  } catch (error) {
    handleError(error);
  }
};

export const statusStyles = {
  Pending: 'bg-[#888] text-white',
  Countered: 'bg-[#ffa000] text-white',
  Approved: 'bg-[#248670] text-white',
  Denied: 'bg-[#b00020] text-white',
  Booked: 'bg-emerald-700 text-white',
  Assigned: 'bg-yellow-100 text-black',
  Deleted: '',
  Expired: 'bg-[#888] text-white',
  Rejected: 'bg-[#b00020] text-white',
};

export const carrierStatusStyles: Record<string, string> = {
  Active: 'text-green-900 bg-[#D3FFD6]',
  Inactive: 'text-red-500 bg-[#EFCCD2] ',
  'Out Of Business': 'text-red-500 bg-[#EFCCD2] ',
  Pending: 'text-gray-500 bg-[#ECEFF1] ',
  Incomplete: 'text-gray-500 bg-[#ECEFF1] ',
  Conditional: 'text-yellow-500 bg-[#FFF4E5] ',
  Unvetted: 'text-gray-500 bg-[#ECEFF1] ',
};

export const amountStyles: Record<string, string> = {
  Countered: 'line-through text-gray-500',
  Pending: 'text-black',
  Approved: 'text-green-900',
  Denied: 'text-red-500',
  Booked: 'text-green-900',
  Assigned: 'text-green-900',
  Deleted: 'text-red-500',
  Expired: 'text-red-500',
  Rejected: 'text-red-500',
};
